/**
 * ALDER TALENT — Head of Product Search Page
 * URL: /senior-leadership/head-of-product-search
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useFaqSchema } from "@/hooks/useFaqSchema";

const headOfProductFaqs = [
  {
    question: "What does a Head of Product do in a VC- and PE-backed business?",
    answer: "A Head of Product in a VC- and PE-backed business owns the product roadmap and is responsible for translating the value creation plan into product priorities. They manage the product team, work closely with engineering and commercial leadership, and are accountable for product-driven revenue growth. In VC- and PE-backed businesses, the Head of Product must be able to make investment decisions under capital constraints and communicate product ROI to a sponsor board.",
  },
  {
    question: "How is a Head of Product search different from a CPO search?",
    answer: "A Head of Product is typically a senior leadership hire focused on execution within a defined product area, reporting to the CPO or CEO. A CPO is a C-suite appointment with board-level accountability for product strategy across the entire business. For VC- and PE-backed businesses at earlier stages of growth, the Head of Product is often the most senior product hire.",
  },
  {
    question: "What experience should a Head of Product have in a VC- and PE-backed environment?",
    answer: "A Head of Product in a VC- and PE-backed environment should have experience building and shipping product in a resource-constrained environment, making prioritisation decisions under pressure, and working with commercial and engineering teams to deliver measurable business outcomes. Experience in a VC- and PE-backed or venture-backed business is an advantage but not a requirement.",
  },
  {
    question: "How long does a Head of Product search take?",
    answer: "A retained Head of Product search typically delivers a shortlist in five to seven weeks and completes placement in eight to twelve weeks. The timeline depends on the seniority of the role, the sector, and the availability of candidates in the market.",
  },
  {
    question: "Do you run Head of Product searches across all sectors?",
    answer: "We run Head of Product searches across all sectors where VC and PE investors hold portfolio companies, with particular depth in SaaS, fintech, e-commerce, and technology-enabled services. Our focus is always VC- and PE-backed businesses, and we assess candidates against the specific product challenges of the portfolio company's stage and sector.",
  }
];


const steps = [
  {
    num: "01",
    title: "Product & Value Creation Alignment",
    body: "We begin by understanding your product, your roadmap, and what the value creation plan requires from the Head of Product. Growth through new features? Retention through product quality? Platform consolidation for exit? The brief is different in each case.",
  },
  {
    num: "02",
    title: "Profile Definition",
    body: "We define the profile together — the background, the stage of product the candidate has operated at, the VC and PE experience required, and the leadership style that fits the engineering and commercial teams they'll be working with.",
  },
  {
    num: "03",
    title: "Market Mapping",
    body: "We map VC- and PE-backed product leaders with a track record of shipping in constrained, pace-driven environments. We look specifically for candidates who've operated at the intersection of commercial and product — not just pure product management backgrounds.",
  },
  {
    num: "04",
    title: "VC- and PE-Specific Assessment",
    body: "We assess for execution discipline, stakeholder management (including sponsor-level communication), and the ability to prioritise ruthlessly. Vision is easy to find. Delivery under VC and PE conditions is not.",
  },
  {
    num: "05",
    title: "Shortlist & Recommendation",
    body: "Two to four candidates, each with a full written assessment against your specific brief. We give a clear recommendation — not just a shortlist.",
  },
  {
    num: "06",
    title: "Placement & First Quarter",
    body: "We manage offer and onboarding, and stay close through the first 90 days. The first quarter is when roadmap decisions get made. We make sure the transition is clean.",
  },
];

const outcomes = [
  "A product leader assessed against your value creation thesis and exit timeline — not a generic product management framework.",
  "Access to VC- and PE-proven product leaders who've operated under sponsor conditions before.",
  "A search that takes weeks, not months — because product velocity is often the constraint.",
];

export default function HeadOfProductPage() {
  useFaqSchema(headOfProductFaqs, "hop-faq-jsonld");
  useEffect(() => {
    const prev = document.title;
    document.title = "Head of Product Search for VC- and PE-Backed Companies | Alder Talent";
    const meta = document.querySelector('meta[name="description"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "Retained Head of Product search for VC- and PE-backed businesses. We place product leaders who execute value creation roadmaps under VC and PE conditions. Alder Talent."
    );
    return () => {
      document.title = prev;
      meta?.setAttribute("content", prevContent);
    };
  }, []);

  const handleEngageClick = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/#contact";
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: "linear-gradient(160deg, oklch(0.18 0.055 155) 0%, oklch(0.245 0.065 155) 100%)" }}
      >
        <div className="container relative z-10">
          <div className="mb-4 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Link href="/" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Home</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <Link href="/senior-leadership" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Senior Leadership</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <span style={{ color: "oklch(0.62 0.085 75)" }}>Head of Product Search</span>
          </div>
          <h1 className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 1.1, color: "oklch(0.97 0.006 80)", maxWidth: "760px" }}>
            The Head of Product Who Ships the Roadmap — Not Just Writes It.
          </h1>
          <p className="mb-10" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "clamp(1rem, 2vw, 1.15rem)", lineHeight: 1.75, color: "oklch(0.78 0.018 80)", maxWidth: "580px" }}>
            A Head of Product in a VC- and PE-backed business needs to translate a sponsor's value creation thesis into a product roadmap, then execute against it under VC and PE conditions. Most product leaders have never done that. We find the ones who have.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={handleEngageClick} className="px-8 py-3.5 transition-all duration-300"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
              Engage Alder Talent
            </button>
            <Link href="/senior-leadership" className="px-8 py-3.5 transition-all duration-300"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.75rem", background: "transparent", color: "oklch(0.88 0.012 80)", border: "1px solid oklch(0.45 0.07 155)", borderRadius: "2px", textDecoration: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.62 0.085 75)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.45 0.07 155)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.012 80)"; }}>
              ← Back to Senior Leadership
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES STRIP */}
      <section className="py-14" style={{ background: "oklch(0.245 0.065 155)", borderBottom: "1px solid oklch(0.32 0.07 155)" }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((text, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="mt-1 shrink-0" style={{ width: "20px", height: "20px", borderRadius: "50%", background: "oklch(0.62 0.085 75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="oklch(0.18 0.055 155)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.65, color: "oklch(0.82 0.015 80)" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24" style={{ background: "oklch(0.97 0.006 80)" }}>
        <div className="container">
          <div className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>How This Search Works</div>
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.245 0.065 155)", maxWidth: "640px" }}>
            Head of Product Search Calibrated to Your Product Thesis.
          </h2>
          <p className="mb-16" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)", maxWidth: "560px" }}>
            The Head of Product role in VC and PE is misunderstood. It's not about vision. It's about execution under constraint. We search for the second type.
          </p>
          <div className="flex flex-col gap-0">
            {steps.map((step) => (
              <div key={step.num} className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 py-8" style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 400, fontSize: "0.72rem", letterSpacing: "0.1em", color: "oklch(0.62 0.085 75)", paddingTop: "3px" }}>{step.num}</div>
                <div>
                  <h3 className="mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: "1.05rem", color: "oklch(0.245 0.065 155)" }}>{step.title}</h3>
                  <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)" }}>{step.body}</p>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }} />
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div className="mx-auto" style={{ maxWidth: "680px", textAlign: "center" }}>
            <div className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: "clamp(1.15rem, 2.5vw, 1.55rem)", lineHeight: 1.6, color: "oklch(0.88 0.012 80)" }}>
              "The Head of Product who writes a great roadmap isn't hard to find. The one who executes it in a VC- and PE-backed business is."
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>
              Alder Talent, Senior Leadership Practice
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="contact" className="py-24" style={{ background: "oklch(0.245 0.065 155)" }}>
        <div className="container text-center">
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.97 0.006 80)" }}>
            Need a Head of Product Who Gets the VC & PE Context?
          </h2>
          <p className="mb-10 mx-auto" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.72 0.018 80)", maxWidth: "520px" }}>
            If you need a product leader who understands the sponsor environment as well as the product brief — let's talk.
          </p>
          <button onClick={handleEngageClick} className="px-10 py-4 transition-all duration-300"
            style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
            Engage Alder Talent
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
